import { useEffect, useState } from "react";
import CircleTorunamentAvatar from "../../../utils/tournament/tournament_profile_img";
import { jwtDecode } from "jwt-decode";
import { useSelector } from "react-redux";
import { showToast } from "../../../components/ToastNotification";
import {
  FaPhoneAlt,
  FaCity,
  FaUsers,
  FaTiktok,
  FaInstagram,
  FaFacebook,
} from "react-icons/fa";
import ModalEditTeam from "../../../components/ModalEditTeam";

const DetailsTab = ({ team }) => {
  const token = useSelector((state) => state.user.token);

  const [userId, setUserId] = useState("");
  const [localTeam, setLocalTeam] = useState(team);
  const [preview, setPreview] = useState(team.picture_url);
  const [image, setImage] = useState(null);
  const [showUpdateButton, setShowUpdateButton] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const isTeamOwner = userId === localTeam.user_id;

  useEffect(() => {
    if (token) {
      const decoded = jwtDecode(token);
      setUserId(decoded.id);
    }
  }, [token]);

  useEffect(() => {
    setLocalTeam(team);
  }, [team]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImage(file);
    setShowUpdateButton(true);

    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleUpdateImage = async () => {
    if (!image) return;

    const formData = new FormData();
    formData.append("team_image", image);

    try {
      const res = await fetch(
        `https://server-postgre.vercel.app/api/teams/${localTeam.id}/image`,
        {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        },
      );

      const data = await res.json();
      if (!res.ok) throw new Error(data.message);

      showToast.success("Imagen actualizada");
      setShowUpdateButton(false);
      setImage(null);

      setLocalTeam((prev) => ({ ...prev, picture_url: preview }));
    } catch (error) {
      console.error(error);
      showToast.error("Error al actualizar imagen");
    }
  };

  return (
    <div className="max-w-3xl mx-auto bg-gray-50 rounded-2xl shadow-lg p-6 space-y-6">
      <div className="flex justify-center">
        {isTeamOwner ? (
          <CircleTorunamentAvatar
            size={140}
            imageSrc={preview}
            onImageSelect={handleImageChange}
            className="border-4 border-gradient-to-r from-blue-400 via-purple-500 to-pink-500 shadow-xl"
          />
        ) : preview ? (
          <img
            src={preview}
            alt="Team"
            className="w-36 h-36 rounded-full object-cover border-4 border-emerald-400 shadow-md"
          />
        ) : (
          <div className="w-36 h-36 rounded-full flex items-center justify-center bg-gray-200 border-4 border-emerald-400 shadow-md">
            <FaUsers size={70} color="#16a34a" />
          </div>
        )}
      </div>

      {isTeamOwner && showUpdateButton && (
        <div className="flex justify-center">
          <button
            onClick={handleUpdateImage}
            className="bg-purple-600 text-white px-6 py-2 rounded-full font-semibold shadow hover:bg-purple-700"
          >
            Actualizar Imagen
          </button>
        </div>
      )}

      <h2 className="text-2xl font-bold text-gray-800 text-center">
        {localTeam.team_name}
      </h2>

      {isTeamOwner && (
        <div className="flex justify-center">
          <button
            onClick={() => setShowEditModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-md shadow hover:bg-blue-700 text-sm"
          >
            Editar información
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex items-center p-3 bg-white rounded-xl shadow">
          <FaPhoneAlt className="text-green-500 mr-3" />
          {localTeam.phone || "No especificado"}
        </div>

        <div className="flex items-center p-3 bg-white rounded-xl shadow">
          <FaCity className="text-emerald-500 mr-3" />
          <strong>Ciudad:</strong> {localTeam.city || "No especificado"}
        </div>

        {(localTeam.facebook || localTeam.instagram || localTeam.tiktok) && (
          <div className="col-span-2 p-3 bg-white rounded-xl shadow space-y-2">
            <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide text-center">
              Redes sociales
            </h3>

            <div className="flex items-center gap-5 justify-center">
              {localTeam.facebook && (
                <a
                  href={
                    localTeam.facebook.startsWith("http")
                      ? localTeam.facebook
                      : `https://facebook.com/${localTeam.facebook}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 group"
                >
                  <FaFacebook
                    size={24}
                    className="text-blue-600 group-hover:scale-110 transition"
                  />
                  <span className="text-sm text-blue-700 group-hover:underline">
                    Facebook
                  </span>
                </a>
              )}

              {localTeam.instagram && (
                <a
                  href={
                    localTeam.instagram.startsWith("http")
                      ? localTeam.instagram
                      : `https://instagram.com/${localTeam.instagram}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 group"
                >
                  <FaInstagram
                    size={24}
                    className="text-pink-600 group-hover:scale-110 transition"
                  />
                  <span className="text-sm text-pink-700 group-hover:underline">
                    Instagram
                  </span>
                </a>
              )}

              {localTeam.tiktok && (
                <a
                  href={
                    localTeam.tiktok.startsWith("http")
                      ? localTeam.tiktok
                      : `https://tiktok.com/@${localTeam.tiktok}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 group"
                >
                  <FaTiktok
                    size={24}
                    className="text-black group-hover:scale-110 transition"
                  />
                  <span className="text-sm text-gray-800 group-hover:underline">
                    TikTok
                  </span>
                </a>
              )}
            </div>
          </div>
        )}
      </div>

      {showEditModal && (
        <ModalEditTeam
          team={localTeam}
          token={token}
          onClose={() => setShowEditModal(false)}
          onUpdated={(updated) => {
            setLocalTeam(updated);
          }}
        />
      )}
    </div>
  );
};

export default DetailsTab;
